/**
 * TrackMergeService.js
 *
 * Service for merging multiple GPS tracks into a single track.
 * Supports three merge modes:
 * - connect: Single LineString with all coordinates concatenated
 * - multiline: MultiLineString where each track is a separate segment
 * - collect: FeatureCollection with separate Features (max flexibility)
 *
 * @module TrackMergeService
 */

import { toRaw } from 'vue';
import { useTrackedRoutesStore } from '@/stores/trackedRoutes';
import { appDebugLog } from '@/utils/appDebugLog';
import { calculateTotalDistance, getPointCount } from '@/utils/TrackMetadataService';

/**
 * Default maximum number of tracks that can be merged at once
 * (Can be overridden by settings)
 */
export const DEFAULT_MAX_MERGE_TRACKS = 10;

/**
 * Merge mode options
 * @enum {string}
 */
export const MERGE_MODES = {
  /** Single continuous LineString - all coordinates concatenated */
  CONNECT: 'connect',
  /** MultiLineString - each track is a segment (current default) */
  MULTILINE: 'multiline',
  /** FeatureCollection - separate Features with own properties */
  COLLECT: 'collect'
};

/**
 * Merge mode UI metadata (SSOT for labels + descriptions).
 * Keep presentation (icons) inside UI components.
 */
export const MERGE_MODE_OPTIONS = [
  {
    value: MERGE_MODES.COLLECT,
    label: 'Collect',
    description: 'Keep tracks separate with individual colors and properties. Best for multi-day trips.',
    confirmDescription: 'Tracks will be kept as separate segments with individual properties.'
  },
  {
    value: MERGE_MODES.CONNECT,
    label: 'Connect',
    description: 'Create one continuous track by connecting all segments end-to-end.',
    confirmDescription: 'All tracks will be joined into one continuous line.'
  },
  {
    value: MERGE_MODES.MULTILINE,
    label: 'Bundle',
    description: 'Bundle tracks as segments in a single feature. Simple but limited styling.',
    confirmDescription: 'Tracks will be bundled into a single multi-segment feature.'
  }
];

export function getMergeModeOption (mergeMode) {
  return MERGE_MODE_OPTIONS.find(option => option.value === mergeMode) || null;
}

/**
 * Validate that tracks can be merged
 * @param {Array<Object>} tracks - Array of track objects with geojson property
 * @param {number} [maxTracks] - Maximum allowed tracks (defaults to DEFAULT_MAX_MERGE_TRACKS)
 * @returns {{valid: boolean, error: string|null}} Validation result
 */
export function validateTracksForMerge (tracks, maxTracks = DEFAULT_MAX_MERGE_TRACKS) {
  if (!Array.isArray(tracks)) {
    return { valid: false, error: 'Tracks must be an array' };
  }

  if (tracks.length < 2) {
    return { valid: false, error: 'At least 2 tracks are required for merging' };
  }

  if (tracks.length > maxTracks) {
    return { valid: false, error: `Maximum ${maxTracks} tracks can be merged at once` };
  }

  // Validate each track has required data
  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i];

    if (!track) {
      return { valid: false, error: `Track at index ${i} is undefined` };
    }

    if (!track.geojson) {
      return { valid: false, error: `Track "${track.title || i}" is missing GeoJSON data` };
    }

    // Check for valid geometry
    const geojson = track.geojson;
    if (!geojson.type) {
      return { valid: false, error: `Track "${track.title || i}" has invalid GeoJSON (missing type)` };
    }

    // Handle both Feature and FeatureCollection
    let geometry = null;
    if (geojson.type === 'Feature') {
      geometry = geojson.geometry;
    }
    else if (geojson.type === 'FeatureCollection' && geojson.features?.length > 0) {
      geometry = geojson.features[0].geometry;
    }
    else if (geojson.type === 'LineString' || geojson.type === 'MultiLineString') {
      geometry = geojson;
    }

    if (!geometry) {
      return { valid: false, error: `Track "${track.title || i}" has no valid geometry` };
    }

    if (geometry.type !== 'LineString' && geometry.type !== 'MultiLineString') {
      return { valid: false, error: `Track "${track.title || i}" must be a LineString or MultiLineString, got ${geometry.type}` };
    }
  }

  return { valid: true, error: null };
}

/**
 * Extract coordinates from a track's GeoJSON
 * Returns array of coordinate arrays (handles both LineString and MultiLineString)
 *
 * @param {Object} geojson - GeoJSON Feature, FeatureCollection, or Geometry
 * @returns {Array<Array<Array<number>>>} Array of line coordinate arrays
 */
function extractLineCoordinates (geojson) {
  let geometry = null;

  // Handle different GeoJSON structures
  if (geojson.type === 'Feature') {
    geometry = geojson.geometry;
  }
  else if (geojson.type === 'FeatureCollection' && geojson.features?.length > 0) {
    geometry = geojson.features[0].geometry;
  }
  else if (geojson.type === 'LineString' || geojson.type === 'MultiLineString') {
    geometry = geojson;
  }

  if (!geometry) return [];

  // LineString has single coordinate array, wrap in outer array
  if (geometry.type === 'LineString') {
    return [geometry.coordinates];
  }

  // MultiLineString already has array of coordinate arrays
  if (geometry.type === 'MultiLineString') {
    return geometry.coordinates;
  }

  return [];
}

/**
 * Extract flat coordinates from a track's GeoJSON (for connect mode)
 * Returns single array of all coordinates from all segments
 *
 * @param {Object} geojson - GeoJSON Feature, FeatureCollection, or Geometry
 * @returns {Array<Array<number>>} Flat array of coordinates
 */
function extractFlatCoordinates (geojson) {
  const lineCoords = extractLineCoordinates(geojson);
  return lineCoords.flat();
}

/**
 * Extract geometry from a track's GeoJSON
 *
 * @param {Object} geojson - GeoJSON Feature, FeatureCollection, or Geometry
 * @returns {Object|null} Geometry object or null
 */
function extractGeometry (geojson) {
  if (geojson.type === 'Feature') {
    return geojson.geometry;
  }
  else if (geojson.type === 'FeatureCollection' && geojson.features?.length > 0) {
    return geojson.features[0].geometry;
  }
  else if (geojson.type === 'LineString' || geojson.type === 'MultiLineString') {
    return geojson;
  }
  return null;
}

/**
 * Extract relevant properties from a track for preservation in merged result
 *
 * @param {Object} track - Track object
 * @returns {Object} Extracted properties
 */
function extractTrackProperties (track) {
  const geojson = track.geojson;
  let geoProps = {};

  // Extract properties from GeoJSON if available
  if (geojson?.type === 'Feature' && geojson.properties) {
    geoProps = { ...geojson.properties };
  }
  else if (geojson?.type === 'FeatureCollection' && geojson.features?.[0]?.properties) {
    geoProps = { ...geojson.features[0].properties };
  }

  return {
    ...geoProps,
    startTime: geoProps.startTime || track.startTime,
    endTime: geoProps.endTime || track.endTime,
    totalDistance: geoProps.totalDistance || track.totalDistance,
    pointCount: track.pointCount
  };
}

/**
 * Get track statistics for a single track
 *
 * @param {Object} track - Track object with geojson
 * @returns {Object} Statistics object
 */
function getTrackStats (track) {
  return {
    id: track.id,
    title: track.title || `Track ${track.id}`,
    activityType: track.activityType || 'Unknown',
    pointCount: getPointCount(track.geojson),
    date: track.date || track.created,
    // Duration and distance would require coordinate parsing
    // which is done in TrackMetadataService
  };
}

// ============================================================================
// Merge Mode Implementations
// ============================================================================

/**
 * Get date range from tracks
 * @param {Array<Object>} tracks - Array of track objects
 * @returns {{firstDate: string, lastDate: string}} Date range
 */
function getDateRange (tracks) {
  const dates = tracks
    .map(t => t.date || t.created)
    .filter(Boolean)
    .sort();

  const firstDate = dates[0] || new Date().toISOString();
  const lastDate = dates[dates.length - 1] || firstDate;

  return { firstDate, lastDate };
}

/**
 * Aggregate metadata from source tracks for the merged track
 * This ensures the display component can show proper values
 *
 * @param {Array<Object>} tracks - Source tracks
 * @param {string} activityType - Activity type for the merged track
 * @returns {Object} Aggregated metadata for feature properties
 */
function aggregateMergeMetadata (tracks, activityType = 'Mixed') {
  // Get earliest startTime and latest endTime
  let earliestStart = null;
  let latestEnd = null;
  let totalDistanceM = 0;
  let totalDistanceKm = 0;

  // Collect unique activity types from tracks
  const uniqueTypes = new Set();

  for (const track of tracks) {
    const props = track.geojson?.features?.[0]?.properties || {};

    // Collect activity types
    const trackType = props.typeTitle || track.activityType || track.typeTitle;
    if (trackType && trackType !== 'Unknown') {
      uniqueTypes.add(trackType);
    }

    // Aggregate times
    if (props.startTime && (!earliestStart || props.startTime < earliestStart)) {
      earliestStart = props.startTime;
    }
    if (props.endTime && (!latestEnd || props.endTime > latestEnd)) {
      latestEnd = props.endTime;
    }

    // Aggregate distance
    // IMPORTANT: Values may be strings from calculateTotalDistance(), so use parseFloat()
    if (props.totalDistance) {
      if (typeof props.totalDistance === 'object') {
        totalDistanceM += parseFloat(props.totalDistance.m) || 0;
        totalDistanceKm += parseFloat(props.totalDistance.km) || 0;
      }
      else if (typeof props.totalDistance === 'number') {
        totalDistanceM += props.totalDistance;
      }
      else if (typeof props.totalDistance === 'string') {
        totalDistanceM += parseFloat(props.totalDistance) || 0;
      }
    }
    else if (track.totalDistance) {
      if (typeof track.totalDistance === 'object') {
        totalDistanceM += parseFloat(track.totalDistance.m) || 0;
        totalDistanceKm += parseFloat(track.totalDistance.km) || 0;
      }
    }
  }

  // If we calculated km from m values, recalculate km for accuracy
  if (totalDistanceM > 0 && totalDistanceKm === 0) {
    totalDistanceKm = totalDistanceM / 1000;
  }

  // Build combined typeTitle: single type stays as-is, multiple become "Biking, Walking"
  let combinedTypeTitle;
  const typeArray = Array.from(uniqueTypes);
  if (typeArray.length === 0) {
    combinedTypeTitle = activityType; // Fallback to provided activityType
  }
  else if (typeArray.length === 1) {
    combinedTypeTitle = typeArray[0];
  }
  else {
    combinedTypeTitle = typeArray.join(', ');
  }

  return {
    typeTitle: combinedTypeTitle,
    startTime: earliestStart,
    endTime: latestEnd,
    totalDistance: totalDistanceM > 0 ? {
      m: Math.round(totalDistanceM * 100) / 100,
      km: Math.round(totalDistanceKm * 100) / 100
    } : null
  };
}

/**
 * Create a connected LineString (CONNECT mode)
 * All track coordinates are concatenated into a single continuous line
 *
 * @param {Array<Object>} tracks - Array of track objects in merge order
 * @returns {Object} GeoJSON FeatureCollection with single LineString
 */
function createConnectedLineString (tracks, activityType = 'Mixed') {
  const allCoordinates = [];

  for (const track of tracks) {
    const coords = extractFlatCoordinates(track.geojson);
    allCoordinates.push(...coords);
  }

  const { firstDate, lastDate } = getDateRange(tracks);
  const metadata = aggregateMergeMetadata(tracks, activityType);

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: allCoordinates
      },
      properties: {
        merged: true,
        mergeMode: MERGE_MODES.CONNECT,
        mergeCount: tracks.length,
        mergedTrackIds: tracks.map(t => t.id),
        mergedTrackTitles: tracks.map(t => t.title || `Track ${t.id}`),
        dateStart: firstDate,
        dateEnd: lastDate,
        activityTypes: [...new Set(tracks.map(t => t.activityType).filter(Boolean))],
        totalPoints: allCoordinates.length,
        // Include metadata for TrackedRoute display
        ...metadata
      }
    }]
  };
}

/**
 * Create a MultiLineString (MULTILINE mode - legacy default)
 * Each track becomes a separate segment within a single Feature
 *
 * @param {Array<Object>} tracks - Array of track objects in merge order
 * @returns {Object} GeoJSON FeatureCollection with MultiLineString
 */
function createMultiLineString (tracks, activityType = 'Mixed') {
  const allLineCoordinates = [];

  for (const track of tracks) {
    const lineCoords = extractLineCoordinates(track.geojson);
    allLineCoordinates.push(...lineCoords);
  }

  const { firstDate, lastDate } = getDateRange(tracks);
  const metadata = aggregateMergeMetadata(tracks, activityType);

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: {
        type: 'MultiLineString',
        coordinates: allLineCoordinates
      },
      properties: {
        merged: true,
        mergeMode: MERGE_MODES.MULTILINE,
        mergeCount: tracks.length,
        mergedTrackIds: tracks.map(t => t.id),
        mergedTrackTitles: tracks.map(t => t.title || `Track ${t.id}`),
        dateStart: firstDate,
        dateEnd: lastDate,
        activityTypes: [...new Set(tracks.map(t => t.activityType).filter(Boolean))],
        // Include metadata for TrackedRoute display
        ...metadata
      }
    }]
  };
}

/**
 * Create a FeatureCollection (COLLECT mode)
 * Each track is a separate Feature with its own properties
 * Optionally adds connector lines between tracks
 *
 * @param {Array<Object>} tracks - Array of track objects in merge order
 * @param {Object} options - Options
 * @param {boolean} [options.showConnectors=false] - Whether to add connector lines
 * @param {Object} [options.connectorStyle] - Connector styling info (stored, not applied)
 * @returns {Object} GeoJSON FeatureCollection with separate Features
 */
function createFeatureCollection (tracks, options = {}) {
  const { showConnectors = false, activityType = 'Mixed' } = options;
  const features = [];
  const { firstDate, lastDate } = getDateRange(tracks);
  const metadata = aggregateMergeMetadata(tracks, activityType);

  tracks.forEach((track, index) => {
    const geometry = extractGeometry(track.geojson);
    if (!geometry) return;

    // Build properties - include merged metadata in FIRST feature for TrackedRoute display
    const baseProperties = {
      featureType: 'track',
      segmentIndex: index,
      originalId: track.id,
      originalTitle: track.title || `Track ${track.id}`,
      date: track.date || track.created,
      activityType: track.activityType || track.typeTitle || 'Unknown',
      color: track.color || null,
      // Preserve extended properties
      ...extractTrackProperties(track)
    };

    // First feature gets the merged metadata for TrackedRoute component
    if (index === 0) {
      Object.assign(baseProperties, {
        merged: true,
        mergeMode: MERGE_MODES.COLLECT,
        mergeCount: tracks.length,
        ...metadata  // typeTitle, startTime, endTime, totalDistance
      });
    }

    features.push({
      type: 'Feature',
      geometry: { ...geometry }, // Clone to avoid mutation
      properties: baseProperties
    });

    // Add connector to next track if enabled
    if (showConnectors && index < tracks.length - 1) {
      const nextGeometry = extractGeometry(tracks[index + 1].geojson);
      if (!nextGeometry) return;

      // Get end point of current track and start point of next track
      const currentCoords = geometry.type === 'MultiLineString'
        ? geometry.coordinates[geometry.coordinates.length - 1]
        : geometry.coordinates;
      const nextCoords = nextGeometry.type === 'MultiLineString'
        ? nextGeometry.coordinates[0]
        : nextGeometry.coordinates;

      const thisEnd = currentCoords[currentCoords.length - 1];
      const nextStart = nextCoords[0];

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [thisEnd, nextStart]
        },
        properties: {
          featureType: 'connector',
          connectorIndex: index,
          fromTrackId: track.id,
          toTrackId: tracks[index + 1].id,
          fromTrackTitle: track.title || `Track ${track.id}`,
          toTrackTitle: tracks[index + 1].title || `Track ${tracks[index + 1].id}`
        }
      });
    }
  });

  return {
    type: 'FeatureCollection',
    features,
    // Store collection-level metadata (non-standard but useful)
    properties: {
      merged: true,
      mergeMode: MERGE_MODES.COLLECT,
      mergeCount: tracks.length,
      hasConnectors: showConnectors,
      mergedTrackIds: tracks.map(t => t.id),
      mergedTrackTitles: tracks.map(t => t.title || `Track ${t.id}`),
      dateStart: firstDate,
      dateEnd: lastDate,
      activityTypes: [...new Set(tracks.map(t => t.activityType).filter(Boolean))],
      ...metadata
    }
  };
}

// ============================================================================
// Main Merge Function
// ============================================================================

/**
 * Merge multiple tracks using the specified mode
 *
 * @param {Array<Object>} tracks - Array of track objects in merge order
 * @param {Object} [options] - Merge options
 * @param {string} [options.mergeMode='multiline'] - Merge mode: 'connect', 'multiline', or 'collect'
 * @param {boolean} [options.showConnectors=false] - Show connector lines (collect mode only)
 * @returns {Object} Merged GeoJSON FeatureCollection
 * @throws {Error} If validation fails or unknown merge mode
 */
export function mergeTracks (tracks, options = {}) {
  const {
    mergeMode = MERGE_MODES.MULTILINE,
    showConnectors = false,
    activityType = 'Mixed'
  } = options;

  const validation = validateTracksForMerge(tracks);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  appDebugLog.info('TrackMergeService', `Merging ${tracks.length} tracks with mode: ${mergeMode}`);

  switch (mergeMode) {
    case MERGE_MODES.CONNECT:
      return createConnectedLineString(tracks, activityType);

    case MERGE_MODES.MULTILINE:
      return createMultiLineString(tracks, activityType);

    case MERGE_MODES.COLLECT:
      return createFeatureCollection(tracks, { showConnectors, activityType });

    default:
      throw new Error(`Unknown merge mode: ${mergeMode}. Use 'connect', 'multiline', or 'collect'.`);
  }
}

/**
 * Generate a smart title for the merged track based on component tracks
 *
 * @param {Array<Object>} tracks - Array of track objects
 * @param {string} [mergeMode='multiline'] - The merge mode being used
 * @returns {string} Generated title
 */
export function generateMergedTitle (tracks, mergeMode = MERGE_MODES.MULTILINE) {
  if (tracks.length === 0) return 'merged-tracks';

  const firstTrack = tracks[0];
  const count = tracks.length;

  // Extract base name from first track
  let baseName = firstTrack.title || `track-${firstTrack.id}`;

  // Remove existing merge prefixes
  baseName = baseName.replace(/^(merged-|collected-|connected-)/, '');

  // Truncate if too long
  if (baseName.length > 30) {
    baseName = baseName.substring(0, 30);
  }

  // Build final name based on merge mode
  let prefix;
  switch (mergeMode) {
    case MERGE_MODES.CONNECT:
      prefix = 'connected';
      break;
    case MERGE_MODES.COLLECT:
      prefix = 'collected';
      break;
    case MERGE_MODES.MULTILINE:
    default:
      prefix = 'merged';
      break;
  }

  return `${prefix}-${baseName}-1-to-${count}`;
}

/**
 * Generate a legacy-style title (for backwards compatibility)
 *
 * @param {Array<Object>} tracks - Array of track objects
 * @returns {string} Generated title
 */
export function generateLegacyTitle (tracks) {
  // Collect unique activity types
  const activityTypes = [...new Set(
    tracks
      .map(t => t.activityType)
      .filter(Boolean)
  )];

  // Format: "geojson-merged__[activity1]_[activity2]__[date]"
  const activityPart = activityTypes.length > 0
    ? activityTypes.join('_')
    : 'mixed';

  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  return `geojson-merged__${activityPart}__${date}`;
}

/**
 * Get summary statistics for merge preview
 *
 * @param {Array<Object>} tracks - Array of track objects
 * @returns {Object} Summary statistics
 */
export function getMergePreviewStats (tracks) {
  const trackStats = tracks.map(getTrackStats);

  const totalPoints = trackStats.reduce((sum, t) => sum + t.pointCount, 0);
  const activityTypes = [...new Set(trackStats.map(t => t.activityType))];

  return {
    trackCount: tracks.length,
    totalPoints,
    activityTypes,
    tracks: trackStats
  };
}

/**
 * Save merged track to database
 *
 * @param {Array<Object>} tracks - Array of track objects to merge
 * @param {Object} options - Save options
 * @param {string} [options.title] - Custom title (auto-generated if not provided)
 * @param {string} [options.mergeMode='multiline'] - Merge mode: 'connect', 'multiline', or 'collect'
 * @param {boolean} [options.showConnectors=false] - Show connector lines (collect mode only)
 * @param {string} [options.activityType] - Activity type (defaults to "Mixed")
 * @param {number} [options.activityTypeId] - Activity type ID
 * @returns {Promise<{success: boolean, trackId: number|null, error: string|null, totalDistance: number|null, pointCount: number|null}>}
 */
export async function saveMergedTrack (tracks, options = {}) {
  try {
    const {
      title,
      mergeMode = MERGE_MODES.MULTILINE,
      showConnectors = false,
      activityType = 'Mixed',
      activityTypeId = null
    } = options;

    const validation = validateTracksForMerge(tracks);
    if (!validation.valid) {
      return { success: false, trackId: null, error: validation.error };
    }

    // Merge using specified mode - pass activityType for metadata aggregation
    const mergedGeojson = mergeTracks(tracks, { mergeMode, showConnectors, activityType });

    // Generate title if not provided
    const finalTitle = title || generateMergedTitle(tracks, mergeMode);

    // Extract stats from merged GeoJSON properties for track-level storage
    const geoProps = mergedGeojson.features?.[0]?.properties || {};
    const totalPoints = getPointCount(mergedGeojson);
    if (mergedGeojson.features?.[0]?.properties) {
      mergedGeojson.features[0].properties.totalPoints = totalPoints;
    }

    // FALLBACK: If totalDistance wasn't aggregated from source tracks, calculate from merged coordinates
    // This happens when source tracks don't have distance data (e.g., old imports, corrupted data)
    let finalTotalDistance = geoProps.totalDistance;
    if (!finalTotalDistance) {
      // For COLLECT mode, we have multiple features - sum ALL their distances
      const features = mergedGeojson.features || [];
      let totalKm = 0;
      let totalM = 0;

      for (const feature of features) {
        // Skip connector features
        if (feature.properties?.featureType === 'connector') continue;

        const geometry = feature.geometry;
        if (geometry?.coordinates) {
          const featureDistance = calculateTotalDistance(geometry.coordinates);
          totalKm += parseFloat(featureDistance.km) || 0;
          totalM += parseFloat(featureDistance.m) || 0;
        }
      }

      if (totalKm > 0) {
        finalTotalDistance = {
          km: Math.round(totalKm * 1000) / 1000,
          m: Math.round(totalM * 1000) / 1000
        };
        // Update the GeoJSON properties so the overlay display works
        if (mergedGeojson.features?.[0]?.properties) {
          mergedGeojson.features[0].properties.totalDistance = finalTotalDistance;
        }
        appDebugLog.info('TrackMergeService', 'Calculated distance from coordinates (all features):', finalTotalDistance);
      }
    }

    // Calculate duration from startTime/endTime for list display
    let durationInSeconds = 0;
    let timerDisplay = '00:00:00';
    if (geoProps.startTime && geoProps.endTime) {
      const start = new Date(geoProps.startTime).getTime();
      const end = new Date(geoProps.endTime).getTime();
      if (end > start) {
        durationInSeconds = Math.floor((end - start) / 1000);
        const h = Math.floor(durationInSeconds / 3600);
        const m = Math.floor((durationInSeconds % 3600) / 60);
        const s = durationInSeconds % 60;
        timerDisplay = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      }
    }

    // Build track record
    // IMPORTANT: Use toRaw() to strip Vue reactivity - IndexedDB cannot clone reactive proxies
    const trackRecord = {
      title: finalTitle,
      activityType,
      activityTypeId,
      geojson: JSON.parse(JSON.stringify(toRaw(mergedGeojson))),  // Deep clone to remove reactivity
      date: new Date().toISOString(),
      created: new Date().toISOString(),
      // Store aggregate stats at track level for list display
      totalDistance: finalTotalDistance || null,
      pointCount: totalPoints,
      startTime: geoProps.startTime || null,
      endTime: geoProps.endTime || null,
      durationInSeconds,
      durationStartStop: durationInSeconds,
      timerDisplay,
      // Store merge metadata
      isMerged: true,
      mergeMode,
      hasConnectors: showConnectors,
      sourceTrackIds: tracks.map(t => toRaw(t).id),
      sourceTrackCount: tracks.length
    };

    // Save via store (SSOT) - handles DB + state update atomically
    const trackedRoutesStore = useTrackedRoutesStore();
    const trackId = await trackedRoutesStore.addTrack(trackRecord);

    if (typeof trackId === 'number') {
      appDebugLog.info('TrackMergeService', `Saved ${mergeMode} merged track with ID:`, trackId);

      // Return stats for success panel display
      const returnedDistance = finalTotalDistance?.km || null;
      const pointCount = totalPoints;

      return { success: true, trackId, error: null, totalDistance: returnedDistance, pointCount };
    }
    else {
      return { success: false, trackId: null, error: 'Failed to save track to database' };
    }
  }
  catch (error) {
    appDebugLog.error('[TrackMergeService] Error saving merged track:', error);
    return { success: false, trackId: null, error: error.message };
  }
}

/**
 * Get color for a track based on its activity type and index
 * (for same-type tracks, use variant colors)
 *
 * @param {Array<Object>} tracks - All tracks being merged
 * @param {number} trackIndex - Index of this track in the array
 * @param {Function} getColorVariant - Color variant getter from activityTypes store
 * @returns {string} Hex color code
 */
export function getTrackDisplayColor (tracks, trackIndex, getColorVariant) {
  const track = tracks[trackIndex];
  const activityType = track.activityType || 'Unknown';

  // Count how many tracks of same type appear before this one
  let sameTypeBefore = 0;
  for (let i = 0; i < trackIndex; i++) {
    if (tracks[i].activityType === activityType) {
      sameTypeBefore++;
    }
  }

  // Use variant index (0, 1, 2 = base, light, lighter)
  const variantIndex = sameTypeBefore % 3;

  return getColorVariant(activityType, variantIndex);
}

export default {
  DEFAULT_MAX_MERGE_TRACKS,
  MERGE_MODES,
  validateTracksForMerge,
  mergeTracks,
  generateMergedTitle,
  generateLegacyTitle,
  getMergePreviewStats,
  saveMergedTrack,
  getTrackDisplayColor
};
